import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Header from "../Header";
import Field from "../../../../../Settings/Content/Components/Field";
import TrashBucketIcon from "../../../../../../Components/Icon-SVG/TrashBucketIcon";
import Tooltip from "../../../../../../Components/common/Tooltip/Tooltip";
import Backdrop from "../../../../../../Components/UI/Backdrop/Backdrop";

import { RequestUpdateAgentPolicy } from "../../../../../../store/actions/integrationsActions";
import { addNotification } from "../../../../../../store/actions/notifierActions";

const MIN_MAX_VALUES = {
  opacity: [1, 99],
  csBonus: [1, 99],
  fontSize: [1, 64],
  rotationAngle: [1, 359],
};

const TextWatermarkForm = ({ selectedHost }) => {
  const dispatch = useDispatch();

  const policy = useSelector((state) => state.integrations.agentPolicy);
  const updatePolicyLoading = useSelector((state) => state.integrations.updatePolicyLoading);

  const [textWatermarkUI, setTextWatermarkUI] = useState({
    ...policy?.settings?.watermark,
    targetApps: policy?.settings?.watermark.targetApps.split(","),
    targetAllApps:
      policy?.settings?.watermark?.targetAllApps === true ||
      policy?.settings?.watermark?.targetAllApps === "true" ||
      policy?.settings?.watermark?.targetAllApps === "True",
  });
  const [targetApp, setTargetApp] = useState("");

  const inputChangeHandler = (e) => {
    const name = e.target.name;
    let value = e.target.value;

    if (name === "targetAllApps") {
      value = value === "true";
    } else {
      value = value === "" ? "" : Number(value);
    }

    setTextWatermarkUI((prevSettings) => ({
      ...prevSettings,
      [name]: value,
    }));
  };

  const blurHandler = (e) => {
    const { name, value } = e.target;
    const limits = MIN_MAX_VALUES[name];
    if (!limits) return;

    const [min, max] = limits;
    let newValue = Number(value);

    if (newValue < min) newValue = min;
    if (newValue > max) newValue = max;

    setTextWatermarkUI((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  const targetAppAddHandler = () => {
    const targetApps = textWatermarkUI.targetApps;
    const trimmedTargetApp = targetApp.trim();

    if (trimmedTargetApp === "") {
      dispatch(
        addNotification({
          message: "Enter a valid target app",
          options: { variant: "error" },
          time: 3000,
        }),
      );
    } else if (targetApps.some((app) => app === trimmedTargetApp)) {
      dispatch(
        addNotification({
          message: "Target app already exists",
          options: { variant: "error" },
          time: 3000,
        }),
      );
    } else {
      setTextWatermarkUI((prevSettings) => ({
        ...prevSettings,
        targetApps: [...prevSettings.targetApps, trimmedTargetApp],
      }));
      setTargetApp("");
    }
  };

  const removeTargetAppHandler = (index) => {
    setTextWatermarkUI((prevSettings) => ({
      ...prevSettings,
      targetApps: prevSettings.targetApps.filter((_, i) => i !== index),
    }));
  };

  const checkSaveButtonDisabled = () => {
    const backend = policy?.settings?.watermark;
    const ui = textWatermarkUI;

    if (!backend || !ui) return true;

    if (
      backend.opacity !== ui.opacity ||
      backend.csBonus !== ui.csBonus ||
      backend.fontSize !== ui.fontSize ||
      backend.rotationAngle !== ui.rotationAngle ||
      backend.targetAllApps !== ui.targetAllApps
    ) {
      return false;
    }

    const backendApps = backend.targetApps.split(",").sort();
    const uiApps = [...(ui.targetApps || [])].sort();

    if (backendApps.length !== uiApps.length) return false;

    const sameApps = backendApps.every((app, index) => app === uiApps[index]);

    return sameApps;
  };

  const updateAgentSettingsHandler = (e) => {
    e.preventDefault();
    if (updatePolicyLoading) return;

    dispatch(
      RequestUpdateAgentPolicy({
        id: policy.id,
        settings: {
          ...policy.settings,
          watermark: { ...textWatermarkUI, targetApps: textWatermarkUI.targetApps.join(",") },
        },
        host: selectedHost,
      }),
    );
  };

  const toggleStatusHandler = () => {
    dispatch(
      RequestUpdateAgentPolicy({
        id: policy.id,
        settings: {
          ...policy.settings,
          watermark: {
            ...policy.settings.watermark,
            enabled: !policy?.settings?.watermark?.enabled,
          },
        },
        host: selectedHost,
      }),
    );
  };

  return (
    <>
      <Header
        policy={policy}
        selectedHost={selectedHost}
        toggleProps={{
          show: true,
          text: "Text Watermark Status",
          active: policy?.settings?.watermark?.enabled,
          onClick: toggleStatusHandler,
        }}
      />
      <form className="agent-policy-form">
        <div className="agent-policy-form__line">
          <Field
            label="Opacity"
            tooltipText="Min: 1, Max: 99"
            width="220px"
            name="opacity"
            value={textWatermarkUI.opacity}
            onChange={inputChangeHandler}
            onBlur={blurHandler}
            type="number"
          />
          <Field
            label="CS Bonus"
            tooltipText="Extra opacity when login method is 'CS'. Min: 1, Max: 99"
            width="220px"
            name="csBonus"
            value={textWatermarkUI.csBonus}
            onChange={inputChangeHandler}
            onBlur={blurHandler}
            type="number"
          />
        </div>

        <div className="agent-policy-form__line">
          <Field
            label="Font Size"
            tooltipText="Min: 1, Max: 64"
            width="220px"
            name="fontSize"
            value={textWatermarkUI.fontSize}
            onChange={inputChangeHandler}
            onBlur={blurHandler}
            type="number"
          />
          <Field
            label="Rotation Angle"
            tooltipText="Min: 1, Max: 359"
            width="220px"
            name="rotationAngle"
            value={textWatermarkUI.rotationAngle}
            onChange={inputChangeHandler}
            onBlur={blurHandler}
            type="number"
          />
        </div>

        <div className="agent-policy-form__line">
          <div className="mailing__content__security m-b-20">
            <p className="light m-b-10 mailing__content__security__header">
              Target All Apps
              <Tooltip text="If enabled, text watermark will be displayed on all apps and the target app list will be ignored." />
            </p>
            <div className="mailing__content__security__labels">
              <label className="align-items">
                <input
                  className="mailing__content__security__input"
                  type="radio"
                  value={true}
                  name="targetAllApps"
                  checked={textWatermarkUI.targetAllApps === true}
                  onChange={inputChangeHandler}
                />
                <p className="margin-left-12">Enabled</p>
              </label>
              <label className="align-items">
                <input
                  className="mailing__content__security__input"
                  type="radio"
                  value={false}
                  name="targetAllApps"
                  checked={textWatermarkUI.targetAllApps === false}
                  onChange={inputChangeHandler}
                />
                <p className="margin-left-12">Disabled</p>
              </label>
            </div>
          </div>
        </div>

        {textWatermarkUI.targetAllApps !== true && (
          <>
            <div className="agent-policy-form__group-line">
              <Field
                label="Target App"
                tooltipText="Applications where text watermark will be displayed"
                width="300px"
                value={targetApp}
                onChange={(e) => setTargetApp(e.target.value)}
                parentClassName="p-r-20"
                placeholder="Enter a target app (e.g., chrome, winword)"
              />
              <button
                type="button"
                className="products-body-form__button"
                onClick={targetAppAddHandler}>
                +
              </button>
            </div>

            <ul className="agent-policy-form__list" style={{ gridColumn: "span 2" }}>
              {textWatermarkUI?.targetApps?.length > 0 ? (
                textWatermarkUI?.targetApps?.map((targetApp, index) => (
                  <li key={index} className="agent-policy-form__list-item">
                    {targetApp}
                    <button
                      type="button"
                      style={{ display: "flex" }}
                      onClick={() => removeTargetAppHandler(index)}>
                      <TrashBucketIcon width="16" height="16" />
                      <p style={{ marginLeft: "5px" }}>Delete</p>
                    </button>
                  </li>
                ))
              ) : (
                <li className="no-data">&nbsp;No Data</li>
              )}
            </ul>
          </>
        )}

        <button
          className="primary-button"
          disabled={checkSaveButtonDisabled()}
          onClick={updateAgentSettingsHandler}>
          Save
        </button>

        <Backdrop
          spinner={false}
          absolute
          show={!policy?.settings?.watermark?.enabled}
          message="Text watermak settings are inactive! Please click on the status button at the header to activate text watermark settings."
          messageStyle={{ paddingTop: 0 }}
        />
      </form>
    </>
  );
};

export default TextWatermarkForm;
